/**
 * DraftRow — inline-editable empty row for Smartsheet-style Insert.
 * Auto-populates week from start date and computes working_days from
 * (start, finish) using business-day math.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, X, GripVertical } from "lucide-react";
import { STATUS_META, statusOptions } from "./constants";
import { dateToIsoWeek, businessDaysBetween } from "./shared";
import WeekDatePicker from "./WeekDatePicker";

const cellInput = "w-full bg-transparent border-0 outline-none text-xs text-slate-100 placeholder-slate-500 px-1 py-0.5 rounded focus:bg-white/5 focus:ring-1 focus:ring-cyan-400";

export default function DraftRow({ draft, defaultWeek, onChange, onSave, onDiscard, frameStyle, columnOrder }) {
  const [saving, setSaving] = useState(false);
  const readyToSave = !!draft.task_name.trim();

  const update = (patch) => onChange(draft.id, patch);

  const save = async () => {
    if (!readyToSave) { toast.error("Task name is required"); return; }
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); }
  };

  const wd = (parseInt(draft.planned_workers) || 0) * (parseFloat(draft.working_days) || 0);
  // Hours override: if user typed a value, use it; else auto = members × days × 7.5
  const autoH = wd * 7.5;
  const hours = draft.total_hours !== "" && draft.total_hours != null
    ? (parseFloat(draft.total_hours) || 0)
    : autoH;
  const budget = Math.round(hours * 600); // hours × 600 NOK

  const [dragging, setDragging] = useState(false);

  const onDragStart = (e) => {
    e.dataTransfer.setData("application/x-capacity-draft", draft.id);
    e.dataTransfer.setData("text/plain", draft.id); // real-browser fallback
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  };
  const onDragEnd = () => {
    setDragging(false);
    // Defensive: clean any stray ring on all week headers (in case dragleave was missed)
    document.querySelectorAll('[data-testid^="week-group-header-"]').forEach((el) => {
      el.classList.remove("ring-2", "ring-cyan-400", "ring-inset");
    });
  };

  const cellById = {
    week: (
      <td key="week" className="px-2 py-1 bg-slate-800/60 border-r border-slate-700/40">
        <div className="flex items-center gap-1">
          <GripVertical className="w-3 h-3 text-slate-500 flex-shrink-0" title="Drag to another week"/>
          <input value={draft.week} placeholder={defaultWeek || "2026-W31"}
                 onChange={(e) => update({ week: e.target.value })}
                 className={`${cellInput} font-mono w-20 text-xs text-slate-200 font-black`}
                 data-testid={`draft-week-${draft.id}`}/>
        </div>
      </td>
    ),
    task: (
      <td key="task" className="px-3 py-1">
        <input value={draft.task_name} placeholder="Task name…" autoFocus
               onChange={(e) => update({ task_name: e.target.value })}
               onKeyDown={(e) => e.key === "Enter" && save()}
               className={`${cellInput} font-black`}
               data-testid={`draft-name-${draft.id}`}/>
      </td>
    ),
    start: (
      <td key="start" className="px-3 py-1">
        <WeekDatePicker value={draft.expected_start}
                        testId={`draft-start-${draft.id}`}
                        maxDate={draft.expected_finish || null}
                        onChange={(start) => {
                          const patch = { expected_start: start };
                          if (start) patch.week = dateToIsoWeek(start);
                          if (start && draft.expected_finish) {
                            const bd = businessDaysBetween(start, draft.expected_finish);
                            if (bd > 0) patch.working_days = bd;
                          }
                          update(patch);
                        }}/>
      </td>
    ),
    finish: (
      <td key="finish" className="px-3 py-1">
        <WeekDatePicker value={draft.expected_finish}
                        testId={`draft-finish-${draft.id}`}
                        minDate={draft.expected_start || null}
                        onChange={(finish) => {
                          const patch = { expected_finish: finish };
                          if (draft.expected_start && finish) {
                            const bd = businessDaysBetween(draft.expected_start, finish);
                            if (bd > 0) patch.working_days = bd;
                          }
                          update(patch);
                        }}/>
      </td>
    ),
    members: (
      <td key="members" className="px-2 py-1 w-16 text-center">
        <input type="number" min="0" value={draft.planned_workers}
               onChange={(e) => update({ planned_workers: e.target.value })}
               className={`${cellInput} w-12 text-center font-mono text-xs`}
               title="Number of team members"/>
      </td>
    ),
    days: (
      <td key="days" className="px-2 py-1 w-14 text-center">
        <input type="number" step="0.5" min="0" value={draft.working_days}
               onChange={(e) => update({ working_days: e.target.value })}
               className={`${cellInput} w-12 text-center font-mono text-xs`}/>
      </td>
    ),
    hours: (
      <td key="hours" className="px-2 py-1 w-16 text-center">
        <input type="number" step="0.5" min="0" value={draft.total_hours ?? ""}
               placeholder={autoH ? `${autoH.toFixed(1)}` : "auto"}
               onChange={(e) => update({ total_hours: e.target.value })}
               className={`${cellInput} w-14 text-center font-mono text-xs`}
               title="Total hours (blank = auto: members × days × 7.5)"/>
      </td>
    ),
    budget: (
      <td key="budget" className="px-2 py-1 w-24 text-center text-emerald-300 font-mono font-black text-xs whitespace-nowrap"
          title={`Budget = ${hours.toFixed(1)}h × 600 NOK`}>
        {budget.toLocaleString("nb-NO")} <span className="text-emerald-500/70 text-[9px]">NOK</span>
      </td>
    ),
    status: (
      <td key="status" className="px-3 py-1">
        <select value={draft.status}
                onChange={(e) => update({ status: e.target.value })}
                className={`${cellInput} text-xs bg-slate-900/60 rounded`}
                data-testid={`draft-status-${draft.id}`}>
          {statusOptions(draft.status).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
      </td>
    ),
    team: (
      <td key="team" className="px-3 py-1">
        <input value={draft.assigned_team} placeholder="Team name…"
               onChange={(e) => update({ assigned_team: e.target.value })}
               className={`${cellInput} text-xs`}/>
      </td>
    ),
    comments: (
      <td key="comments" className="px-3 py-1 min-w-[280px]">
        <input value={draft.comments} placeholder="Comments — describe the job in detail…"
               onChange={(e) => update({ comments: e.target.value })}
               className={`${cellInput} text-xs w-full`}/>
      </td>
    ),
    actions: (
      <td key="actions" className="px-3 py-1 whitespace-nowrap">
        <div className="flex items-center gap-1 justify-end">
          <button onClick={save} disabled={saving || !readyToSave}
                  className="p-1.5 rounded text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-30"
                  data-testid={`draft-save-${draft.id}`}
                  title="Save row (Enter)">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
          </button>
          <button onClick={() => onDiscard(draft.id)}
                  className="p-1.5 rounded text-slate-400 hover:text-red-300"
                  data-testid={`draft-discard-${draft.id}`}
                  title="Discard draft">
            <X className="w-3.5 h-3.5"/>
          </button>
        </div>
      </td>
    ),
  };

  const order = columnOrder && columnOrder.length ? columnOrder : Object.keys(cellById);

  return (
    <tr className={`border-b border-white/5 bg-cyan-500/5 cursor-move ${dragging ? "opacity-40" : ""}`}
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        style={frameStyle}
        data-testid={`draft-row-${draft.id}`}>
      {order.map((id) => cellById[id]).filter(Boolean)}
    </tr>
  );
}
